import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { SimpleInput } from 'app/shared/input';
import { MultiSelect } from 'app/shared/select';
import Spinner from 'app/shared/spinners/spinner-15/spinner-15';
import { DetailItem } from 'app/shared/info-section/item';
import { GridColumn, GridRow } from 'app/layouts/grid';
import useValidator from 'hooks/validator';
import { useOrganizationService } from 'hooks/organization';
import { organizationValidationConfig } from 'app/shared/utils/form/validation/organization';
import { useNavigate } from 'react-router-dom';
import styles from '../organization.module.css';
import { useDispatch, useSelector } from 'react-redux';
import { addDataToStore } from 'store/actions/user-data';
import { ReactComponent as Icon } from 'assets/icons/logo-sm.svg';
import { industries } from 'app/data/industries';

const CONTACT_CLASSES = [
  { label: '0 - 5,000', value: '0:5000' },
  { label: '5,001 - 20,000', value: '5001:20000' },
  { label: '20,001 - 50,000', value: '20001:50000' },
  { label: '50,001 - 100,000', value: '50001:100000' },
  { label: '100,001 - 250,000', value: '100001:250000' },
  { label: '250,001 - 300,000', value: '250001:300000' },
  { label: '300,001 - 400,000', value: '300001:400000' },
  { label: '400,001 - 500,000', value: '400001:500000' },
  { label: '500,001 - 1,000,000', value: '500001:1000000' },
  { label: '1,000,000+', value: '1000000' }
];

const SCREENS = [
  {
    label: 'Welcome',
    subtitle: 'Reach more customers, pay in Naira, and only pay for what you actually send.'
  },
  {
    label: 'Basics',
    subtitle: "Let's start with the basics. Tell us about your organisation."
  },
  {
    label: 'Contact',
    subtitle: 'How do we send important updates and notifications to you?'
  }
];

const CAPABILITIES = [
  {
    title: 'Pay in Naira, no FX headaches',
    text: 'Top up your wallet in Naira and only pay for the emails and SMS you actually send.'
  },
  {
    title: 'Land in the inbox, not spam',
    text: 'With a 96% inbox delivery rate across local and global networks, your message reaches real people — not the spam folder.'
  },
  {
    title: 'Put your marketing on autopilot',
    text: 'Automate welcome series, follow-ups and drip sequences so customers hear from you at the right time.'
  }
];

export const CreateOrganization = () => {
  const user_data = useSelector((state) => state.user_data);
  const { email: user_email, memberships } = user_data;

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { createOrganization } = useOrganizationService();
  const { errors, validateField } = useValidator(organizationValidationConfig);

  const [screen, setScreen] = useState(1);
  const [is_membership_active, setActiveMembership] = useState(false);
  const [form_data, setFormData] = useState({
    name: '',
    website: '',
    email: '',
    phone_number: ''
  });
  const [saving, setSaving] = useState(false);
  const [selected_subscriber_count, setSelectedSubscriberCount] = useState(CONTACT_CLASSES[0]);
  const [selected_industry, setSelectedIndustry] = useState(industries[0]);

  useEffect(() => {
    if (!memberships || !memberships.length) return;
    const has_active_membership = memberships.some((doc) => doc.status === 'active');
    setActiveMembership(has_active_membership);
  }, [memberships]);

  const basics_valid = !!form_data.name && !errors.name && !!form_data.website && !errors.website;

  const contact_valid =
    !!form_data.email && !errors.email && !!form_data.phone_number && !errors.phone_number;

  const screen_is_valid = screen === 2 ? basics_valid : screen === 3 ? contact_valid : true;

  const is_last_screen = screen === SCREENS.length;

  const goNext = () => setScreen((prev) => Math.min(prev + 1, SCREENS.length));
  const goBack = () => setScreen((prev) => Math.max(prev - 1, 1));

  const submitForm = async () => {
    const data = {
      emails: [form_data.email],
      industry: selected_industry.value,
      name: form_data.name,
      phone_number: form_data.phone_number,
      subscriber_count: selected_subscriber_count.value,
      user_email,
      website: form_data.website
    };

    setSaving(true);
    const response = await createOrganization({ data });
    setSaving(false);

    if (!response) return;

    const is_first_org = !memberships || memberships.length === 0;

    dispatch(
      addDataToStore({
        ...user_data,
        tenant_id: response.id,
        token: response.token,
        memberships: response.memberships,
        onboarding_completed: is_first_org ? response.onboarding_completed || false : true,
        logged_in_time: Date.now()
      })
    );
    toast.success('Organisation created successfully.');

    if (is_first_org) {
      navigate('/organizations/qualification');
    } else {
      navigate('/dashboard');
    }
  };

  const handleChange = (field, value) => {
    validateField(field, value);
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubscriberCountChange = (selection) => {
    setSelectedSubscriberCount(selection);
  };

  const handleIndustryChange = (selection) => {
    setSelectedIndustry(selection);
  };

  const handleCancelOrLogout = () => {
    if (is_membership_active) {
      navigate('/dashboard');
    } else {
      dispatch(addDataToStore({}));
      navigate('/login');
    }
  };

  const { subtitle } = SCREENS[screen - 1];

  return (
    <div className={styles.wrapper}>
      <div className={styles.header}>
        <Icon />
        <span className={styles.siteName} aria-label="Go-Mailer">
          Go-Mailer<span className={styles.stub}>.</span>
        </span>
      </div>

      <div className={styles.formWrapper}>
        <div className={styles.steps}>
          {SCREENS.map((s, i) => (
            <div key={i} className={styles.stepSegmentWrap}>
              <div
                className={`${styles.stepSegment}${
                  screen >= i + 1 ? ` ${styles.stepSegmentFilled}` : ''
                }`}
              />
              <span
                className={`${styles.stepSegmentLabel}${
                  screen === i + 1 ? ` ${styles.stepSegmentLabelActive}` : ''
                }`}
              >
                {s.label}
              </span>
            </div>
          ))}
        </div>

        <div className={styles.formTitle}>Create your organisation</div>
        <p className={styles.subtitle}>{subtitle}</p>

        {screen === 1 && (
          <div className={styles.featureList}>
            {CAPABILITIES.map((capability) => (
              <div key={capability.title} className={styles.featureItem}>
                <div className={styles.featureItemContent}>
                  <span className={styles.featureTitle}>{capability.title}</span>
                  <span className={styles.featureText}>{capability.text}</span>
                </div>
              </div>
            ))}
          </div>
        )}

        {screen === 2 && (
          <>
            <GridRow num_of_columns={1}>
              <GridColumn>
                <DetailItem dark title="Organisation Name" required>
                  <SimpleInput
                    dark
                    input_id="name"
                    value={form_data.name}
                    onInput={(value) => handleChange('name', value)}
                    error={errors.name}
                  />
                </DetailItem>
              </GridColumn>
            </GridRow>
            <GridRow num_of_columns={2}>
              <GridColumn>
                <DetailItem dark title="Website" required>
                  <SimpleInput
                    dark
                    input_id="website"
                    value={form_data.website}
                    onInput={(value) => handleChange('website', value)}
                    error={errors.website}
                  />
                </DetailItem>
              </GridColumn>
              <GridColumn>
                <DetailItem dark title="Estimated List Size" required>
                  <MultiSelect
                    dark
                    input_id="subscribers"
                    options={CONTACT_CLASSES}
                    value={selected_subscriber_count}
                    onChange={handleSubscriberCountChange}
                    isSorted={false}
                  />
                </DetailItem>
              </GridColumn>
            </GridRow>
            <GridRow num_of_columns={1}>
              <GridColumn>
                <DetailItem dark title="Industry" required>
                  <MultiSelect
                    dark
                    input_id="industry"
                    options={industries}
                    value={selected_industry}
                    onChange={handleIndustryChange}
                  />
                </DetailItem>
              </GridColumn>
            </GridRow>
          </>
        )}

        {screen === 3 && (
          <>
            <GridRow num_of_columns={1}>
              <GridColumn>
                <DetailItem dark title="Contact Email" required>
                  <SimpleInput
                    dark
                    input_id="email-address"
                    value={form_data.email}
                    onInput={(value) => handleChange('email', value)}
                    error={errors.email}
                  />
                </DetailItem>
              </GridColumn>
            </GridRow>
            <GridRow num_of_columns={1}>
              <GridColumn>
                <DetailItem dark title="Phone Number" required>
                  <SimpleInput
                    dark
                    input_id="phone-number"
                    value={form_data.phone_number}
                    onInput={(value) => handleChange('phone_number', value)}
                    error={errors.phone_number}
                  />
                  <p className={styles.subtitle} style={{ margin: '6px 0 0', fontSize: '0.75rem' }}>
                    For account security and support.
                  </p>
                </DetailItem>
              </GridColumn>
            </GridRow>
          </>
        )}

        <div className={styles.actionRow}>
          <GridRow num_of_columns={2}>
            <GridColumn>
              {screen === 1 ? (
                <button className={styles.btnSecondary} onClick={handleCancelOrLogout}>
                  Cancel
                </button>
              ) : (
                <button className={styles.btnSecondary} onClick={goBack}>
                  ← Back
                </button>
              )}
            </GridColumn>
            <GridColumn>
              {is_last_screen ? (
                <button
                  className={styles.btnPrimary}
                  onClick={submitForm}
                  disabled={saving || !screen_is_valid}
                >
                  {saving ? <Spinner /> : 'Continue →'}
                </button>
              ) : (
                <button className={styles.btnPrimary} onClick={goNext} disabled={!screen_is_valid}>
                  {screen === 1 ? 'Get started →' : 'Continue →'}
                </button>
              )}
            </GridColumn>
          </GridRow>
        </div>
      </div>
    </div>
  );
};
